import React from "react";
var __jsx = React.createElement;
import { useState, useMemo } from "react";
import { useTranslation } from "@i18n";
import ProgressiveImage from "@componentsShared/ProgressiveImage";
import useHelpers from "@hooks/useHelpers";
import useIsMobile from "@hooks/useIsMobile";
import { twMerge } from "tailwind-merge";
import { URL_SELL_TRADE_FORM_v2 } from "@/pages/sell-trade-v2/sell-trade/constants";
import { trackEvents } from "@/common/events/events";
import { getPlatformInfo } from "@/utils/GlobalJavascriptFunction";
import { useLang } from "@/common/Contexts/LanguageContext";
import brandsTreeAr from "@data/lookups/brands_tree_ar.json";
import brandsTreeEn from "@data/lookups/brands_tree_en.json";
import useCheckABFlag from "@hooks/useCheckABFlag";
var CarsMakes = function CarsMakes(_ref) {
  var data = _ref.data,
    selectedMakeId = _ref.selectedMakeId,
    onSelectMake = _ref.onSelectMake,
    onShowMore = _ref.onShowMore;
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var _useTranslation = useTranslation(),
    tShort = _useTranslation.tShort;
  var _useHelpers2 = useHelpers(),
    label = _useHelpers2.label;
  var lang = useLang();
  var isMobile = useIsMobile();
  var isABTestEnabled = useCheckABFlag("AB_SELL_MY_CAR_V3");
  var _useState = useState(null),
    internalSelectedMakeId = _useState[0],
    setInternalSelectedMakeId = _useState[1];

  // Get brands data from imported JSON files
  var brandsData = useMemo(function () {
    var data = lang === "ar" ? brandsTreeAr : brandsTreeEn;
    return data.makes.map(function (make) {
      return {
        id: String(make.id),
        name_ar: make.name,
        name_en: make.name,
        logo: make.logo,
        searchable_text: make.searchable_text
      };
    });
  }, [lang]);
  var carMakes = (data === null || data === void 0 ? void 0 : data.payloads) || brandsData;

  // Use external state if provided, otherwise use internal state
  var currentSelectedMakeId = selectedMakeId !== undefined ? selectedMakeId : internalSelectedMakeId;
  var handleSelectMake = function handleSelectMake(makeId) {
    trackEvents("sell_car_form_landing", {
      platform: getPlatformInfo(),
      "referral_location": "logo_icon",
      version: isABTestEnabled && "3"
    });
    navigateTo(URL_SELL_TRADE_FORM_v2, {
      make_id: makeId
    });
  };
  var initialMaxItems = isMobile ? 7 : 11;
  var initialMaxMakes = initialMaxItems; // Reserve last slot for "show more"

  var displayedMakes = carMakes.slice(0, initialMaxMakes);
  if (carMakes.length === 0) return null;
  var handleShowMoreClick = function handleShowMoreClick() {
    trackEvents("sell_car_form_landing", {
      platform: getPlatformInfo(),
      "referral_location": "logo_icon",
      version: isABTestEnabled && "3"
    });
    navigateTo(URL_SELL_TRADE_FORM_v2);
  };
  return /*#__PURE__*/React.createElement("div", {
    id: "car-makes-section",
    className: "mb-10 md:my-10 mx-4 md:mx-auto md:w-[70%] shadow-lg md:shadow-none rounded-xl border-[1px] border-solid border-[#D6DFE9]"
  }, /*#__PURE__*/React.createElement("h2", {
    id: "car-makes-title",
    className: "text-primary-new text-[20px] font-bold leading-[32px] md:text-[32px] text-center pt-4 md:pt-8"
  }, tShort("makes/title", {
    ns: "sell_trade_landing"
  })), /*#__PURE__*/React.createElement("div", {
    id: "car-makes-container",
    className: "mb-8  m-auto w-[85%] md:w-[70%]"
  }, /*#__PURE__*/React.createElement("p", {
    id: "car-makes-subtitle",
    className: "text-sm md:text-2xl text-center py-4"
  }, (data === null || data === void 0 ? void 0 : data.subtitle) || (lang === "ar" ? "حدد نوع سيارتك" : "Select your car type")), /*#__PURE__*/React.createElement("div", {
    id: "car-makes-grid",
    className: "grid grid-cols-4 md:grid-cols-6 gap-3 md:gap-4 lg:gap-6 mt-4"
  }, displayedMakes.map(function (make, index) {
    var isSelected = currentSelectedMakeId === make.id;
    return /*#__PURE__*/React.createElement("div", {
      id: "car-make-item-".concat(make.id),
      key: make.id,
      onClick: function onClick() {
        return handleSelectMake(make.id);
      },
      className: twMerge("flex justify-center items-center flex-col cursor-pointer transition-all duration-200", isSelected ? "opacity-80 scale-95" : "hover:opacity-80"),
      "aria-label": label(make)
    }, /*#__PURE__*/React.createElement("div", {
      id: "car-make-logo-container-".concat(make.id),
      className: twMerge("h-[56px] w-[56px] md:h-[90px] md:w-[90px] lg:h-[118px] lg:w-[118px]  relative flex justify-center items-center overflow-hidden transition-all duration-200 rounded-lg border-[1px] border-solid border-[#D6DFE9]", isSelected ? "bg-primary-new bg-opacity-10 border-2 border-primary-new" : "bg-[#FFFFFF]")
    }, /*#__PURE__*/React.createElement(ProgressiveImage, {
      waitUntilInteractive: true,
      fetchpriority: "low",
      width: 90,
      height: 90,
      src: make.logo,
      alt: label(make),
      loading: "lazy",
      imageClassName: twMerge(isMobile ? "w-[40px]" : "w-[50%]", " object-contain")
    })), /*#__PURE__*/React.createElement("span", {
      id: "car-make-name-".concat(make.id),
      className: ["h-10 md:text-xl mt-[8px] text-center text-sm font-normal leading-[16px] transition-colors duration-200", isSelected ? "text-primary-new font-semibold" : "text-[#484848]"].join(" ")
    }, label(make)));
  }), /*#__PURE__*/React.createElement("div", {
    id: "car-makes-show-more-button",
    onClick: handleShowMoreClick,
    className: "flex justify-center items-center flex-col cursor-pointer hover:opacity-80 transition-all duration-200",
    "aria-label": "Show more makes"
  }, /*#__PURE__*/React.createElement("div", {
    id: "car-makes-show-more-icon-container",
    className: "relative grid place-items-center overflow-hidden h-[56px] w-[56px] md:h-[90px] md:w-[90px] lg:h-[118px] lg:w-[118px] rounded-lg border-[1px] border-solid border-[#D6DFE9]"
  }, /*#__PURE__*/React.createElement("span", {
    id: "car-makes-show-more-icon",
    className: "text-4xl relative top-[-16%] md:top-[-8%]"
  }, "....")), /*#__PURE__*/React.createElement("span", {
    id: "car-makes-show-more-text",
    className: "h-10 md:text-xl mt-[8px] text-center text-sm font-normal leading-[16px] transition-colors duration-200 text-[#484848]"
  }, tShort("makes/more", {
    ns: "sell_trade_landing"
  }))))), /*#__PURE__*/React.createElement("div", {
    id: "car-makes-next-step-link",
    onClick: handleShowMoreClick,
    className: "cursor-pointer text-[16px] text-primary-new text-center my-6"
  }, tShort("makes/next_step_add_car_details", {
    ns: "sell_trade_landing"
  })));
};
export default CarsMakes;